/**
 * Global axios interceptor that injects `Authorization: Bearer <token>` on every API request.
 *
 * Why: cross-domain cookies (samesite=none) are increasingly unreliable due to browser
 * partitioning policies (Chrome CHIPS, Safari ITP, Firefox dynamic state partitioning).
 * Bearer tokens via Authorization header are immune to these policies and always work
 * cross-domain. Backend already accepts both — cookie (legacy) and Authorization header.
 *
 * Setup: imported once from `src/index.js` at app boot. Idempotent.
 */
import axios from 'axios';

const SESSION_TOKEN_KEY = 'session_token';

export const getStoredToken = () => {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

export const setStoredToken = (token) => {
  try {
    if (token) localStorage.setItem(SESSION_TOKEN_KEY, token);
    else localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    /* ignore — storage may be disabled in private mode */
  }
};

export const clearStoredToken = () => setStoredToken(null);

let installed = false;
export const installAuthInterceptor = () => {
  if (installed) return;
  installed = true;
  axios.interceptors.request.use((config) => {
    // Only attach to our backend API calls (URL contains /api/) — never to 3rd-party URLs
    const url = config.url || '';
    if (url.includes('/api/')) {
      const token = getStoredToken();
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  });
};
